import React, { Suspense, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import Backdrop from 'components/common/backdrop/CustomBackdrop';
import SimulatorStepper from './SimulatorStepper';
import SimulatorSwitcher from './SimulatorSwitcher';
import { initialSteps, stepCodes, initialFilters } from '../constants';
import SimulatorAssetSelectionContainer from './assetSelection/SimulatorAssetSelectionContainer';
import SimulatorOfferContainer from './offer/SimulatorOfferContainer';
import { initAssetData } from 'features/asset/hierarchy/actionCreators';
import { loadSimulatorAssets, resetSimulatorAssets } from '../actionCreators';
import { withRouter } from 'react-router-dom';
import HeaderPortal from 'components/layout/HeaderPortal';
import curry from 'lodash.curry';
import { FakeText } from '@totalsoft/rocket-ui';

const SimulatorContainer = ({ actions, defaultAsset, assets, savingOffer, history, location, t }) => {
  const [steps, setSteps] = useState(initialSteps);
  const [assetInfo, setAssetInfo] = useState(null);
  const [activeStepCode, setActiveStepCode] = useState(stepCodes.ASSET_SELECTION);
  const [filterValues, setFilterValues] = useState(initialFilters);
  const [hasFinancial, setHasFinancial] = useState(false);

  useEffect(() => {
    actions.initAssetData();
    return () => {
      actions.resetSimulatorAssets();
    };
  }, [actions]);

  useEffect(() => {
    if (assetInfo) {
      setSteps(prev => prev.map(x => (x.code === stepCodes.OFFER ? { ...x, disabled: false } : x)));
    } else {
      setHasFinancial(false);
      setActiveStepCode(stepCodes.ASSET_SELECTION);
      setSteps(prev => prev.map(x => (x.code === stepCodes.OFFER ? { ...x, disabled: true } : x)));
    }
  }, [assetInfo]);

  useEffect(() => {
    if (defaultAsset && defaultAsset.assetTypeId && filterValues.assetTypeId !== defaultAsset.assetTypeId)
      setFilterValues({
        ...filterValues,
        assetTypeId: defaultAsset.assetTypeId
      });
  }, [defaultAsset, filterValues]);

  useEffect(() => {
    if (activeStepCode === stepCodes.ASSET_SELECTION) setAssetInfo(null);
  }, [activeStepCode]);

  const handleStepChange = useCallback(
    step => {
      if (location.pathname === step.route) return;
      const financialStep = steps.find(x => x.code === stepCodes.OFFER);
      if (location.pathname !== financialStep?.route || window.confirm(t('Simulator.Step.LeavingFinancial'))) {
        history.push(step.route);
        setActiveStepCode(step.code);
      }
    },
    [history, location, steps, t]
  );

  const handleSliderPropertyChange = curry((prop, value) => {
    switch (prop) {
      case 'assetTypeId': {
        if (filterValues[prop] === value.assetTypeId) {
          break;
        }
        setFilterValues({
          ...filterValues,
          [prop]: value.assetTypeId
        });
        break;
      }
      case 'isSortDescending': {
        if (filterValues[prop] === value.id) {
          break;
        }
        setFilterValues({
          ...filterValues,
          [prop]: value.id
        });
        break;
      }
      default: {
        if (filterValues[prop] === value) {
          break;
        }
        setFilterValues({
          ...filterValues,
          [prop]: value
        });

        break;
      }
    }
  });

  const handleAssetInfoPropertyChange = curry((prop, value) => {
    if (assetInfo[prop] === value) {
      return;
    }
    setAssetInfo({
      ...assetInfo,
      [prop]: value
    });
  });

  const handleAssetSelected = useCallback(
    (asset, imageUrl) => {
      setAssetInfo({
        ...filterValues,
        ...asset,
        imageUrl
      });
      setHasFinancial(true);
      setActiveStepCode(stepCodes.OFFER);
      handleStepChange(steps.find(x => x.code === stepCodes.OFFER));
    },
    [filterValues, handleStepChange, steps]
  );

  const simulatorAssetContainer = useCallback(
    () => (
      <SimulatorAssetSelectionContainer
        assets={assets}
        assetInfo={assetInfo}
        filterValues={filterValues}
        savingOffer={savingOffer}
        onAssetSelected={handleAssetSelected}
        onSliderPropertyChange={handleSliderPropertyChange}
      />
    ),
    [assets, assetInfo, filterValues, savingOffer, handleAssetSelected, handleSliderPropertyChange]
  );

  const simulatorFinancialContainer = useCallback(
    () => (
      <SimulatorOfferContainer
        assetInfo={assetInfo}
        savingOffer={savingOffer}
        handleAssetInfoFiltersChange={handleAssetInfoPropertyChange}
      />
    ),
    [assetInfo, savingOffer, handleAssetInfoPropertyChange]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SimulatorStepper steps={steps} activeStepCode={activeStepCode} onStepClick={handleStepChange} />
      </Grid>
      <Grid item xs={12}>
        <Suspense fallback={<FakeText lines={10} onPaper />}>
          <SimulatorSwitcher
            simulatorAssetContainer={simulatorAssetContainer}
            simulatorFinancialContainer={simulatorFinancialContainer}
            hasFinancial={hasFinancial}
          />
        </Suspense>
      </Grid>
      <Backdrop message={t('Simulator.OfferSavingMessage')} open={savingOffer} />
      <HeaderPortal />
    </Grid>
  );
};

SimulatorContainer.propTypes = {
  defaultAsset: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired,
  images: PropTypes.object,
  actions: PropTypes.object.isRequired,
  savingOffer: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        initAssetData,
        loadSimulatorAssets,
        resetSimulatorAssets
      },
      dispatch
    )
  };
}

function mapStateToProps(state) {
  const defaultAsset = state.assetData.defaultAsset;
  const assets = state.simulatorData.assets;
  const savingOffer = state.simulatorData.saving;

  return {
    defaultAsset,
    assets,
    savingOffer
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withRouter, withTranslation())(SimulatorContainer);
